import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, byColumnUrl } from './config';
import { useTheme } from './theme';
import { useDebug } from './debug';

// AdminBallots
// ----------------------------------------------------------------------------
// Lets admins create / edit / delete ballots from inside the app instead of
// editing the Google Sheet by hand.
//
// Sheet shape (Ballots):
//   name       (string, unique — used as the "type" key by VotePage/VoteTally)
//   display    (string — pretty text shown to voters)
//   audience   (string — 'all' | 'members' | 'board')
//   maxVotes   (number — how many votes a single member is allowed)
//
// SheetDB doesn't have an 'id' column on Ballots, so PATCH/DELETE keys off
// the `name` column via byColumnUrl. That means renaming a ballot = delete +
// recreate, which we surface in the UI.

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone' },
  { value: 'members', label: 'Members only' },
  { value: 'board', label: 'Board only' },
];

const normalizeAudience = (a) => {
  const x = (a || '').toString().trim().toLowerCase();
  if (x === 'board' || x === 'board only' || x === 'board-only') return 'board';
  if (x === 'members' || x === 'members only' || x === 'members-only') return 'members';
  return 'all';
};

const audienceLabel = (a) => {
  const x = normalizeAudience(a);
  if (x === 'board') return 'Board only';
  if (x === 'members') return 'Members only';
  return 'Everyone';
};

const stylesFor = (theme, isMobile = false) => {
  const dark = theme === 'dark';
  return {
    page: {
      padding: isMobile ? 10 : 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
      maxWidth: 1000,
    },
    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    h2: { margin: 0, fontSize: 24, color: dark ? '#f8fafc' : '#0f172a' },
    headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
      fontSize: 14,
    },
    btnPrimary: {
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#0ea5e9' : '#0284c7'}`,
      background: '#0284c7',
      color: '#fff',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 700,
      width: '100%',
    },
    btnDanger: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#7f1d1d' : '#ef4444'}`,
      background: '#991b1b',
      color: 'white',
      cursor: 'pointer',
      fontSize: 14,
    },
    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 1000,
    },
    label: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
      fontWeight: 700,
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? 'rgba(2, 6, 23, 0.55)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
      resize: 'vertical',
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: 10,
    },
    muted: { color: dark ? '#94a3b8' : '#64748b', fontSize: 12 },
    listRow: {
      borderTop: `1px solid ${dark ? 'rgba(148,163,184,0.15)' : '#e5e7eb'}`,
      paddingTop: 12,
      marginTop: 12,
    },
    pill: {
      display: 'inline-block',
      padding: '6px 10px',
      borderRadius: 999,
      border: `1px solid ${dark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.25)'}`,
      background: dark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(248,250,252,0.9)',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 12,
      marginRight: 8,
      marginTop: 6,
      whiteSpace: 'nowrap',
    },
    pillBoard: {
      borderColor: dark ? 'rgba(239,68,68,0.45)' : '#dc2626',
      color: dark ? '#fecaca' : '#991b1b',
      background: dark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
      fontWeight: 800,
    },
    miniActions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
    divider: {
      borderTop: `1px dashed ${dark ? 'rgba(148,163,184,0.25)' : '#e5e7eb'}`,
      margin: '12px 0',
    },
    stickyBar: {
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
      background: dark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(248, 250, 252, 0.92)',
      borderTop: `1px solid ${dark ? 'rgba(148,163,184,0.18)' : '#e5e7eb'}`,
      padding: 10,
      marginTop: 12,
      borderRadius: 14,
      backdropFilter: 'blur(6px)',
    },
  };
};

export default function AdminBallots() {
  const [theme, setTheme] = useTheme();
  const [debug] = useDebug();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 720 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const S = useMemo(() => stylesFor(theme, isMobile), [theme, isMobile]);

  const [loading, setLoading] = useState(true);
  const [ballots, setBallots] = useState([]);

  // Form state
  const [originalName, setOriginalName] = useState(null); // null = creating
  const [name, setName] = useState('');
  const [display, setDisplay] = useState('');
  const [audience, setAudience] = useState('all');
  const [maxVotes, setMaxVotes] = useState(2);
  const [saveStatus, setSaveStatus] = useState('');

  const resetForm = () => {
    setOriginalName(null);
    setName('');
    setDisplay('');
    setAudience('all');
    setMaxVotes(2);
    setSaveStatus('');
  };

  const fetchBallots = async () => {
    setLoading(true);
    try {
      const res = await fetch(sheetUrl('Ballots'), { cache: 'no-store' });
      const data = await res.json();
      const list = (data || [])
        .filter((r) => (r.name || '').toString().trim() !== '')
        .map((r) => ({
          name: (r.name || '').toString().trim(),
          display: (r.display || '').toString().trim(),
          audience: normalizeAudience(r.audience),
          maxVotes: r.maxVotes ? Number(r.maxVotes) : 2,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setBallots(list);
    } catch (e) {
      console.error('AdminBallots: fetch failed', e);
      setBallots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBallots();
  }, []);

  const validate = () => {
    const n = name.trim();
    if (!n) return 'Ballot name (key) is required.';
    if (!/^[A-Za-z0-9_-]+$/.test(n))
      return 'Name can only contain letters, numbers, dashes and underscores.';
    if (n.length > 60) return 'Name must be 60 characters or fewer.';
    const mv = Number(maxVotes);
    if (!Number.isFinite(mv) || mv < 1) return 'Max votes must be at least 1.';
    if (mv > 50) return 'Max votes seems wrong (must be 50 or fewer).';

    const isCreating = !originalName;
    const isRename = originalName && originalName !== n;
    if (isCreating || isRename) {
      const clash = ballots.find(
        (b) => b.name.toLowerCase() === n.toLowerCase() && b.name !== originalName
      );
      if (clash) return `A ballot named "${clash.name}" already exists.`;
    }
    return '';
  };

  const saveBallot = async () => {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }
    setSaveStatus('Saving…');

    const payload = {
      name: name.trim(),
      display: display.trim(),
      audience: normalizeAudience(audience),
      maxVotes: Number(maxVotes) || 2,
    };

    try {
      if (!originalName) {
        // Creating new
        const res = await fetch(sheetUrl('Ballots'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: [payload] }),
        });
        if (!res.ok) throw new Error(await res.text());
      } else if (originalName === payload.name) {
        // Editing in place — PATCH by name column
        const res = await fetch(byColumnUrl('Ballots', 'name', originalName), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        });
        if (!res.ok) throw new Error(await res.text());
      } else {
        // Renamed — SheetDB can't change a key column safely, so delete + create
        const ok = window.confirm(
          `You renamed "${originalName}" to "${payload.name}".\n\n` +
          'This will delete the old ballot row and create a new one. ' +
          'Existing votes already cast under "' + originalName + '" will NOT be moved — ' +
          'they stay attached to the old name in the Votes sheet.\n\nProceed?'
        );
        if (!ok) {
          setSaveStatus('Cancelled.');
          return;
        }
        const delRes = await fetch(byColumnUrl('Ballots', 'name', originalName), {
          method: 'DELETE',
        });
        if (!delRes.ok) throw new Error(await delRes.text());
        const addRes = await fetch(sheetUrl('Ballots'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: [payload] }),
        });
        if (!addRes.ok) throw new Error(await addRes.text());
      }

      setSaveStatus('Saved.');
      await fetchBallots();
      resetForm();
    } catch (e) {
      console.error('AdminBallots: save failed', e);
      setSaveStatus('Failed.');
      alert('Failed to save ballot.\n\n' + (e?.message || ''));
    }
  };

  const startEdit = (b) => {
    setOriginalName(b.name);
    setName(b.name);
    setDisplay(b.display || '');
    setAudience(b.audience || 'all');
    setMaxVotes(b.maxVotes || 2);
    setSaveStatus('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteBallot = async (b) => {
    if (
      !window.confirm(
        `Delete ballot "${b.name}"?\n\n` +
        'Existing vote records in the Votes sheet are not deleted — only the ballot row.'
      )
    ) return;
    try {
      const res = await fetch(byColumnUrl('Ballots', 'name', b.name), {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchBallots();
      if (originalName === b.name) resetForm();
    } catch (e) {
      console.error('AdminBallots: delete failed', e);
      alert('Failed to delete ballot.\n\n' + (e?.message || ''));
    }
  };

  const isEditing = !!originalName;

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Admin</div>
          <h2 style={S.h2}>🗳️ Ballots</h2>
          <div style={S.muted}>
            Create and manage votes. Members see ballots according to audience.
          </div>
        </div>

        <div style={S.headerActions}>
          <button style={S.btn} onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
          </button>
          <button style={S.btn} onClick={fetchBallots}>
            Refresh
          </button>
        </div>
      </div>

      {/* Composer */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800 }}>
            {isEditing ? `Editing: ${originalName}` : 'New ballot'}
          </div>
          {isEditing ? (
            <button style={S.btn} onClick={resetForm}>
              Cancel edit
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={S.label}>Ballot name (unique key)</div>
          <input
            style={S.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. gate_upgrade_2026"
            spellCheck={false}
          />
          {debug && (
            <div style={S.muted}>
              Internal key. Letters, numbers, <code>-</code>, <code>_</code> only. Used to group votes in the Votes sheet.
            </div>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={S.label}>Display (what voters see)</div>
          <textarea
            style={S.textarea}
            rows={3}
            value={display}
            onChange={(e) => setDisplay(e.target.value)}
            placeholder='e.g. "Approve $2500 for gate install"'
          />
        </div>

        <div style={S.divider} />

        <div style={S.grid2}>
          <div>
            <div style={S.label}>Audience</div>
            <select
              style={S.input}
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            >
              {AUDIENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div style={S.muted}>
              <b>Board only</b> ballots are visible to users flagged as board (and admins).
            </div>
          </div>

          <div>
            <div style={S.label}>Max votes per member</div>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              style={S.input}
              value={maxVotes}
              onChange={(e) => setMaxVotes(e.target.value)}
            />
            <div style={S.muted}>
              Most ballots are 1 (single-choice). Use 2+ if a member can vote Yes/No on multiple items.
            </div>
          </div>
        </div>

        <div style={S.stickyBar}>
          <button onClick={saveBallot} style={S.btnPrimary}>
            {isEditing ? 'Save Changes' : 'Create Ballot'}
          </button>
          <div style={{ ...S.muted, marginTop: 6 }}>
            {saveStatus
              ? `Status: ${saveStatus}`
              : debug
              ? (isEditing
                  ? 'Tip: changing the name will replace the row (votes already cast keep the old name).'
                  : 'Tip: pick a short, snake_case name — that becomes the type key in the Votes sheet.')
              : ''}
          </div>
        </div>
      </div>

      {/* List */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 800 }}>Existing ballots</div>
          <div style={S.muted}>
            {loading ? 'Loading…' : `${ballots.length} ballot(s)`}
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: 10, ...S.muted }}>Loading ballots…</div>
        ) : ballots.length === 0 ? (
          <div style={{ marginTop: 10, ...S.muted }}>
            No ballots yet. Create your first one above.
          </div>
        ) : (
          ballots.map((b) => {
            const isBoardOnly = b.audience === 'board';
            return (
              <div key={b.name} style={S.listRow}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{b.name}</div>
                {b.display ? (
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{b.display}</div>
                ) : (
                  <div style={{ ...S.muted, marginTop: 4 }}>(no display text)</div>
                )}
                <div>
                  <span style={{ ...S.pill, ...(isBoardOnly ? S.pillBoard : {}) }}>
                    {audienceLabel(b.audience)}
                  </span>
                  <span style={S.pill}>Max votes: {b.maxVotes}</span>
                </div>
                <div style={S.miniActions}>
                  <button style={S.btn} onClick={() => startEdit(b)}>
                    Edit
                  </button>
                  <button style={S.btnDanger} onClick={() => deleteBallot(b)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
